import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  ArrowReloadHorizontalIcon,
  CancelCircleIcon,
  Invoice03Icon,
  Link03Icon,
  MagicWand04Icon,
  MoneyAdd01Icon,
  MoneySend02Icon,
  MoreVerticalIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import CircularProgress from "@mui/material/CircularProgress";
import { IconButton, Menu, MenuItem, toast } from "@outpave/ds";
import { useFeatureEntitlements } from "@frontegg/react";
import { useNavigate } from "react-router-dom";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import {
  useResolvedBankAccounts,
  UseGetBankAccountsResult,
  BankAccount,
} from "hooks/useGetBankAccounts";
import TransferFundsDrawer from "./TransferFunds";
import AddFundingDrawer from "./AddFundingDrawer";
import AutoBalanceDrawer from "./AutoBalanceDrawer";
import ConnectedFundingAccountDrawer from "./ConnectedFundingAccountDrawer";
import RepayChargeDrawer from "./RepayChargeDrawer";

/** A drawer the panel around this menu can open on its own. */
export type AccountDrawer =
  "connectedAccounts" | "autoBalance" | "addFunds" | "transferFunds" | "repay";

/** A drawer request scoped to one account, for a per-row menu. */
export interface AccountDrawerRequest {
  drawer: AccountDrawer;
  accountId?: string;
}

interface AccountActionsMenuProps {
  selectedBankAccountId?: string;
  /**
   * The account this menu acts on. Its programme decides which actions the
   * menu lists and which account the drawers open against.
   */
  account?: BankAccount;
  /** Charge programs repay a statement; prepaid ones move their own funds. */
  charge?: boolean;
  /**
   * A drawer opened from outside the menu: a dashboard row's own menu, or bank
   * settings' "Balance rules" button. This menu owns all four
   * drawers, so a sibling control asks for one rather than mounting a second
   * copy.
   */
  requested?: AccountDrawer | AccountDrawerRequest | null;
  onRequestedClose?: () => void;
  /**
   * Render the drawers without the kebab that opens them, for a page whose own
   * controls are the only way in. The drawers still need one owner, so this
   * suppresses the trigger rather than the component.
   */
  hideTrigger?: boolean;
  refreshTransactions: () => void;
  // Forwarded from a parent that already fetched accounts; self-fetches when absent.
  bankAccounts?: UseGetBankAccountsResult;
}

/**
 * The accounts panel's overflow menu, and the owner of the four bank drawers
 * it opens. Which actions it lists is the one thing that differs between a
 * prepaid and a charge program, so the two panels this replaced are now one.
 */
const AccountActionsMenu: React.FC<AccountActionsMenuProps> = ({
  selectedBankAccountId,
  account,
  charge = false,
  requested = null,
  onRequestedClose,
  hideTrigger = false,
  refreshTransactions,
  bankAccounts,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEntitled: featureManageBanks } = useFeatureEntitlements(
    "feature_manage_banks",
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openAutoBalanceDrawer, setOpenAutoBalanceDrawer] = useState(false);
  const [openDrawerConnectedFunding, setOpenDrawerConnectedFunding] =
    useState(false);
  const [openTransferDrawer, setOpenTransferDrawer] = useState(false);
  const [openAddFundingDrawer, setOpenAddFundingDrawer] = useState(false);
  const [openRepayDrawer, setOpenRepayDrawer] = useState(false);

  const {
    internalAccounts: financialAccounts,
    externalAccounts: allExternalAccounts,
    isLoading: loading,
    refetchAllAccounts,
  } = useResolvedBankAccounts(bankAccounts, {
    accountTypes: [AccountType.FINANCIAL_ACCOUNT, AccountType.BANK],
  });

  // A shared fetch may include PLAID external cards, which aren't valid transfer
  // or funding sources; keep only linked banks.
  const externalBanks = useMemo(
    () =>
      allExternalAccounts.filter(
        (account) => account.accountType === AccountType.BANK,
      ),
    [allExternalAccounts],
  );
  const fundedAccounts = useMemo(
    () =>
      financialAccounts.filter(
        (row) => row.cardProgram?.capabilities?.moveFunds,
      ),
    [financialAccounts],
  );

  // The account's own programme wins; `charge` stays for the pages that still
  // pass a panel-wide answer.
  const isCharge = account
    ? account.applicationType === ApplicationType.CHARGE
    : charge;
  const requestedDrawer =
    typeof requested === "string" ? requested : (requested?.drawer ?? null);
  const requestedAccountId =
    typeof requested === "string" ? undefined : requested?.accountId;
  const scopedAccountId = requestedAccountId ?? account?.id;
  const scopedAccount = scopedAccountId
    ? financialAccounts.find((row) => row.id === scopedAccountId)
    : account;

  // The program answers for its issuer and the company's plan together.
  const capabilities = account?.cardProgram?.capabilities;

  const closeMenu = () => setAnchorEl(null);

  const handleCloseDrawers = (refresh?: boolean) => {
    if (refresh) refreshTransactions();

    setOpenTransferDrawer(false);
    setOpenAddFundingDrawer(false);
    setOpenAutoBalanceDrawer(false);
    setOpenDrawerConnectedFunding(false);
    setOpenRepayDrawer(false);
    onRequestedClose?.();
  };

  const opens = (open: () => void) => () => {
    open();
    closeMenu();
  };

  const comingSoon = (feature: string) => () => {
    toast.info(t("{{feature}} is coming soon", { feature }));
    closeMenu();
  };

  const prepaidActions = [
    {
      label: t("Add Funds"),
      icon: <HugeiconsIcon icon={MoneyAdd01Icon} size={18} />,
      onClick: opens(() => setOpenAddFundingDrawer(true)),
      shouldNotShow: !capabilities?.moveFunds,
    },
    {
      label: t("Transfer Funds"),
      icon: <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} size={18} />,
      onClick: opens(() => setOpenTransferDrawer(true)),
      shouldNotShow: !capabilities?.moveFunds,
    },
    {
      label: t("Auto Balance"),
      icon: <HugeiconsIcon icon={MagicWand04Icon} size={18} />,
      onClick: opens(() => setOpenAutoBalanceDrawer(true)),
      shouldNotShow: !capabilities?.moveFunds,
    },
  ];

  const chargeActions = [
    {
      label: t("Pay Now"),
      icon: <HugeiconsIcon icon={MoneySend02Icon} size={18} />,
      onClick: opens(() => setOpenRepayDrawer(true)),
      shouldNotShow: !capabilities?.chargeRepayment,
    },
    {
      label: t("Auto Pay"),
      icon: <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={18} />,
      onClick: comingSoon(t("Auto Pay")),
      shouldNotShow: false,
    },
  ];

  const actions = [
    ...(isCharge ? chargeActions : prepaidActions),
    // Prepaid programs get monthly statements as well as charge ones.
    {
      label: t("Statements"),
      icon: <HugeiconsIcon icon={Invoice03Icon} size={18} />,
      onClick: opens(() => navigate("/admin/statements")),
      shouldNotShow: !capabilities?.statements,
    },
    {
      label: t("Connected Accounts"),
      icon: <HugeiconsIcon icon={Link03Icon} size={18} />,
      onClick: opens(() => setOpenDrawerConnectedFunding(true)),
      shouldNotShow: !featureManageBanks,
    },
  ].filter((action) => !action.shouldNotShow);

  return (
    <>
      {!hideTrigger && (
        <>
          <IconButton
            variant="ghost"
            size="small"
            aria-label={t("Account actions")}
            onClick={(event) => {
              if (!loading) setAnchorEl(event.currentTarget);
            }}
          >
            {loading ? (
              <CircularProgress color="primary" size="1.5rem" />
            ) : (
              <HugeiconsIcon icon={MoreVerticalIcon} size={16} />
            )}
          </IconButton>

          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
            {actions.length > 0 ? (
              actions.map((action) => (
                <MenuItem
                  key={action.label}
                  leading={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </MenuItem>
              ))
            ) : (
              // Entitlements can leave the menu with nothing in it. The row used to
              // be clickable and only closed the menu; disabled says the same thing
              // without offering a click that does nothing.
              <MenuItem
                disabled
                leading={<HugeiconsIcon icon={CancelCircleIcon} size={18} />}
              >
                {t("No Actions Possible")}
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      <TransferFundsDrawer
        open={openTransferDrawer || requestedDrawer === "transferFunds"}
        selectedFromAccountId={scopedAccountId}
        handleCloseDrawer={handleCloseDrawers}
        fromAccountsData={fundedAccounts}
        toAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
      />

      <AddFundingDrawer
        selectedBankAccountId={selectedBankAccountId}
        selectedToAccountId={scopedAccountId}
        open={openAddFundingDrawer || requestedDrawer === "addFunds"}
        handleCloseDrawer={handleCloseDrawers}
        toAccountsData={fundedAccounts}
        bankAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
      />

      <AutoBalanceDrawer
        open={openAutoBalanceDrawer || requestedDrawer === "autoBalance"}
        selectedToAccountId={scopedAccountId}
        handleCloseDrawer={handleCloseDrawers}
        fromAccountsData={externalBanks}
        toAccountsData={fundedAccounts}
        refetchAccounts={refetchAllAccounts}
      />

      <RepayChargeDrawer
        open={openRepayDrawer || requestedDrawer === "repay"}
        account={scopedAccount}
        handleCloseDrawer={handleCloseDrawers}
      />

      <ConnectedFundingAccountDrawer
        open={
          openDrawerConnectedFunding || requestedDrawer === "connectedAccounts"
        }
        handleCloseDrawer={handleCloseDrawers}
        bankAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
        program={scopedAccount?.cardProgram}
      />
    </>
  );
};

export default AccountActionsMenu;
